import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./App.css";

import Distribuicao from "./components/Distribuicao";
import EscalaMes from "./components/EscalaMes";
import MatrizApoio from "./components/MatrizApoio";
import NiveisApoio from "./components/NiveisApoio";
import PlantaoAgora from "./components/PlantaoAgora";
import { IconeTema } from "./components/Icones";

import { HORIZONTE_ANOS, RODIZIO } from "./data/team";
import {
  chaveDeHoje,
  distribuicao,
  distribuicaoDoAno,
  hojeUTC,
  plantaoDoDia,
  plantaoDoMes,
  proximosPlantoes,
  rotuloMes,
} from "./lib/schedule";

function useTema() {
  const [tema, setTema] = useState(() => {
    const salvo =
      typeof localStorage !== "undefined" && localStorage.getItem("tema");
    if (salvo) return salvo;
    return typeof matchMedia !== "undefined" &&
      matchMedia("(prefers-color-scheme: light)").matches
      ? "claro"
      : "escuro";
  });

  useEffect(() => {
    document.documentElement.dataset.tema = tema;
    try {
      localStorage.setItem("tema", tema);
    } catch (e) {
      /* modo privado: só não persiste */
    }
  }, [tema]);

  return [tema, () => setTema((t) => (t === "escuro" ? "claro" : "escuro"))];
}

export default function App() {
  const [tema, alternarTema] = useTema();
  const hoje = useMemo(() => hojeUTC(), []);
  const chaveHoje = useMemo(() => chaveDeHoje(), []);

  const [cursor, setCursor] = useState({ ano: hoje.ano, mes: hoje.mes });
  const [filtro, setFiltro] = useState(null);

  // Um ano para trás para consulta, e o horizonte inteiro para frente.
  const limites = useMemo(() => {
    const primeiro = { ano: hoje.ano - 1, mes: 0 };
    const ultimo = { ano: hoje.ano + HORIZONTE_ANOS, mes: 11 };
    const posicao = cursor.ano * 12 + cursor.mes;
    return {
      primeiro,
      ultimo,
      podeVoltar: posicao > primeiro.ano * 12 + primeiro.mes,
      podeAvancar: posicao < ultimo.ano * 12 + ultimo.mes,
    };
  }, [cursor, hoje.ano]);

  const plantaoHoje = useMemo(() => plantaoDoDia(chaveHoje), [chaveHoje]);
  const dias = useMemo(
    () => plantaoDoMes(cursor.ano, cursor.mes),
    [cursor.ano, cursor.mes]
  );
  const cargaMes = useMemo(() => distribuicao(dias), [dias]);
  const cargaAno = useMemo(() => distribuicaoDoAno(cursor.ano), [cursor.ano]);
  const rotulo = rotuloMes(cursor.ano, cursor.mes);

  const proximo = useMemo(
    () => (filtro ? proximosPlantoes(filtro, 1)[0] : null),
    [filtro]
  );

  const navegar = useCallback(
    (meses) => {
      setCursor((c) => {
        const d = new Date(Date.UTC(c.ano, c.mes + meses, 1));
        const alvo = { ano: d.getUTCFullYear(), mes: d.getUTCMonth() };
        const posicao = alvo.ano * 12 + alvo.mes;
        const min = limites.primeiro.ano * 12 + limites.primeiro.mes;
        const max = limites.ultimo.ano * 12 + limites.ultimo.mes;
        if (posicao < min) return limites.primeiro;
        if (posicao > max) return limites.ultimo;
        return alvo;
      });
    },
    [limites]
  );

  const voltarParaHoje = useCallback(
    () => setCursor({ ano: hoje.ano, mes: hoje.mes }),
    [hoje.ano, hoje.mes]
  );

  // Filtro só faz sentido para quem está na fila do rodízio.
  useEffect(() => {
    if (filtro && !RODIZIO.includes(filtro)) setFiltro(null);
  }, [filtro]);

  const noMesAtual = cursor.ano === hoje.ano && cursor.mes === hoje.mes;

  return (
    <div className="app">
      <a className="pular" href="#conteudo">
        Pular para o conteúdo
      </a>

      <header className="topo">
        <div className="topo__marca">
          <span className="topo__produto">Escala de Plantão</span>
          <span className="topo__time">Time AccessControl</span>
        </div>
        <button
          type="button"
          className="botao botao__icone"
          onClick={alternarTema}
          aria-label={tema === "escuro" ? "Usar tema claro" : "Usar tema escuro"}
        >
          <IconeTema escuro={tema === "escuro"} />
        </button>
      </header>

      <main id="conteudo" className="conteudo">
        <PlantaoAgora plantao={plantaoHoje} />

        <NiveisApoio
          apoio={plantaoHoje.apoio}
          titulo="Se não atender, acione nesta ordem"
          descricao={`Escada de ${plantaoHoje.pessoa.primeiroNome} para hoje. Desça um nível por vez.`}
        />

        <EscalaMes
          rotulo={rotulo}
          dias={dias}
          time={RODIZIO}
          filtro={filtro}
          onFiltro={setFiltro}
          onMes={navegar}
          onAno={(passo) => navegar(passo * 12)}
          onHoje={voltarParaHoje}
          chaveHoje={chaveHoje}
          noMesAtual={noMesAtual}
          limites={limites}
          proximo={proximo}
        />

        <Distribuicao
          mes={cargaMes}
          ano={cargaAno}
          rotuloMes={rotulo}
          anoVisto={cursor.ano}
        />

        <MatrizApoio time={RODIZIO} />
      </main>
    </div>
  );
}
